import React, { useState, useEffect } from 'react';
import { Box, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Radio, RadioGroup, IconButton, Button, Typography, CircularProgress } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { v4 as uuidv4 } from 'uuid';
import firebase from "firebase/app";
import "firebase/database";
import "firebase/storage";
import AdvancedImageOptimizer from '../../services/AdvancedImageOptimizer';
import ShortUrlService from '../../services/ShortUrlService';
import AuthenticatedUrlCache from '../../services/AuthenticatedUrlCache';

// Helper function to get display URL for images (handles both legacy and new)
const getDisplayUrl = async (imageUrl) => {
  if (!imageUrl) return '';
  
  // Handle blob URLs (temporary URLs for cloned images) - return as-is
  if (imageUrl.startsWith('blob:')) {
    console.log('🖼️ Using blob URL for display:', imageUrl.substring(0, 50) + '...');
    return imageUrl;
  }
  
  // Handle data URLs (base64 images) - return as-is
  if (imageUrl.startsWith('data:image')) {
    console.log('🖼️ Using data URL for display');
    return imageUrl;
  }
  
  let finalUrl;
  
  // If it's a short URL (starts with q- or img_), convert to full URL
  if (imageUrl.startsWith('q-') || imageUrl.startsWith('img_')) {
    // Try to use ImageUrlMigrationService first, fallback to ShortUrlService
    try {
      const { default: ImageUrlMigrationService } = await import('../../services/ImageUrlMigrationService');
      finalUrl = await ImageUrlMigrationService.expandImageUrl(imageUrl);
    } catch (error) {
      // Fallback to ShortUrlService for legacy support
      finalUrl = await ShortUrlService.toFullUrl(imageUrl);
    }
  } else {
    // Otherwise, return as-is (should be full Firebase Storage URL)
    finalUrl = imageUrl;
  }
  
  // Handle Firebase Storage URLs with authentication
  if (finalUrl.includes('firebasestorage.googleapis.com')) {
    // Always use authenticated access for secure storage
    try {
      const authenticatedUrl = await AuthenticatedUrlCache.getAuthenticatedUrl(finalUrl);
      return authenticatedUrl;
    } catch (error) {
      console.error('Failed to get authenticated URL:', error);
      // Return the original URL as fallback instead of throwing error
      return finalUrl;
    }
  }
  
  return finalUrl;
};

// Authenticated Image Component
const AuthenticatedImage = ({ imageUrl, alt, style }) => {
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);
        const authenticatedUrl = await getDisplayUrl(imageUrl);
        setSrc(authenticatedUrl);
      } catch (err) {
        console.error('Failed to load authenticated image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (imageUrl) {
      loadImage();
    }
  }, [imageUrl]);

  if (loading) {
    return <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>Loading image...</div>;
  }

  if (error) {
    return (
      <div style={{ 
        ...style, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        background: '#ffebee', 
        color: '#d32f2f',
        fontSize: '12px',
        textAlign: 'center',
        padding: '8px'
      }}>
        🔒 Authentication required<br/>
        Please login to view this image
      </div>
    );
  }

  return (
    <img 
      src={src} 
      alt={alt} 
      style={style}
      onError={(e) => {
        console.error('Image failed to load:', src);
        e.target.style.display = 'none';
      }}
    />
  );
};

const QuestionEditor = ({ section, question, sectionIndex, questionIndex, onQuestionChange, onRemoveQuestion, validationErrors = {}, hasInteracted = false }) => {
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Initialize ShortUrlService with Firebase config
  useEffect(() => {
    const firebaseConfig = firebase.app().options;
    ShortUrlService.autoInit(firebaseConfig);
  }, []);

  // Add authentication listener to ensure user is authenticated
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      // User authentication state tracked silently
    });

    return () => unsubscribe();
  }, []);


  // Ensure video question options have proper IDs
  useEffect(() => {
    if (question.type === 'video' && question.options) {
      const optionsWithIds = question.options.map(option => {
        if (!option.id) {
          return { ...option, id: uuidv4() };
        }
        return option;
      });
      
      // Only update if we actually added IDs
      if (optionsWithIds.some((opt, idx) => !question.options[idx].id)) {
        onQuestionChange(question.id, 'options', optionsWithIds);
      }
    }
    // eslint-disable-next-line
  }, [question.type, question.options]);

  const handleOptionChange = (optIndex, field, value) => {
    const updatedOptions = question.options.map((opt, i) =>
      i === optIndex ? { ...opt, [field]: value } : opt
    );
    onQuestionChange(question.id, 'options', updatedOptions);
  };

  const handleCorrectAnswerChange = (correctOptionId) => {
    // For single-choice MCQ, only one option can be correct
    if (question.type === 'mcq-single') {
      const updatedOptions = question.options.map(opt => ({
        ...opt,
        isCorrect: opt.id === correctOptionId
      }));
      onQuestionChange(question.id, 'options', updatedOptions);
    }
  };

  const addOption = () => {
    onQuestionChange(question.id, 'options', [
      ...question.options,
      { id: uuidv4(), text: '', isCorrect: false, marks: 0 }
    ]);
  };

  const removeOption = (optIndex) => {
    if (question.options.length > 1) {
      onQuestionChange(question.id, 'options', question.options.filter((_, i) => i !== optIndex));
    }
  };

  // Store image file for later upload (on submit)
  const handleImageSelection = (file) => {
    if (!file) return;

    // Store the file object for later upload
    onQuestionChange(question.id, 'imageFile', file);
    
    // Create a temporary preview URL for display
    const previewUrl = URL.createObjectURL(file);
    onQuestionChange(question.id, 'imageUrl', previewUrl);
  };

  // Remove image
  const handleRemoveImage = () => {
    // Clear both the file and URL
    onQuestionChange(question.id, 'imageFile', null);
    onQuestionChange(question.id, 'imageUrl', '');
  };



  return (
    <Box key={question.id} border={1} borderRadius={8} p={2} mb={2}>
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 8 }}>
        <Grid item xs={4} sm={4} md={4}>
          <TextField
            label="Question Text"
            value={question.text}
            onChange={e => onQuestionChange(question.id, 'text', e.target.value)}
            variant="outlined"
            fullWidth
            error={!!validationErrors[`question_${sectionIndex}_${questionIndex}_text`]}
            helperText={validationErrors[`question_${sectionIndex}_${questionIndex}_text`] ? validationErrors[`question_${sectionIndex}_${questionIndex}_text`] : ''}
          />
        </Grid>
        <Grid item xs={1} sm={1} md={1}>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleImageSelection(file);
              }
            }}
            style={{ display: 'none' }}
            id={`question-image-${question.id}`}
          />
          <label htmlFor={`question-image-${question.id}`}>
            <IconButton
              component="span"
              size="small"
              style={{ 
                color: question.imageUrl ? '#4caf50' : '#666',
                background: question.imageUrl ? '#e8f5e8' : '#f8f9fa',
                border: question.imageUrl ? '1px solid #4caf50' : '1px solid #e9ecef',
                borderRadius: 6,
                flexShrink: 0,
                height: 40,
                width: 40,
                transition: 'all 0.2s ease'
              }}
              title={question.imageUrl ? "Image attached" : "Add Image"}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </label>
        </Grid>
        <Grid item xs={2} sm={2} md={2}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Type</InputLabel>
            <Select
              value={question.type}
              onChange={e => onQuestionChange(question.id, 'type', e.target.value)}
              label="Type"
            >
              <MenuItem value="mcq-single">Single-Choice MCQ</MenuItem>
              <MenuItem value="mcq-multi">Multi-Choice MCQ</MenuItem>
              <MenuItem value="text">Text-Based</MenuItem>
              <MenuItem value="scenario-text">Scenario-Based Text</MenuItem>
              <MenuItem value="code">Coding</MenuItem>
              <MenuItem value="video">Video Record</MenuItem>
              <MenuItem value="psyco">Psyco</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2} sm={2} md={2}>
          <TextField
            label="Max Score"
            type="number"
            value={question.maxScore !== undefined ? question.maxScore : 1}
            onChange={e => onQuestionChange(question.id, 'maxScore', parseInt(e.target.value) || 1)}
            variant="outlined"
            InputProps={{ inputProps: { min: 1, style: { padding: 6, height: 24 } } }}
            fullWidth
            error={!!validationErrors[`question_${sectionIndex}_${questionIndex}_maxScore`]}
            helperText={validationErrors[`question_${sectionIndex}_${questionIndex}_maxScore`] ? validationErrors[`question_${sectionIndex}_${questionIndex}_maxScore`] : ''}
          />
        </Grid>
        <Grid item xs={1} sm={1} md={1}>
          <IconButton 
            onClick={() => {
          
              onRemoveQuestion();
            }}
            color="secondary"
            style={{ 
              background: '#ffebee', 
              border: '1px solid #f44336',
              marginTop: 8
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
      
      {/* Enhanced Image Preview with Optimization Details */}
      {question.imageUrl && (
        <div style={{ 
          width: '50%',
          marginTop: 8, 
          padding: 16, 
          background: '#f8f9fa', 
          border: '1px solid #e9ecef', 
          borderRadius: 8,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12
        }}>
          <AuthenticatedImage 
            imageUrl={question.imageUrl}
            alt="Question" 
            style={{ 
              width: 80, 
              height: 100, 
              objectFit: 'cover',
              border: '1px solid #ddd',
              borderRadius: 6,
              flexShrink: 0
            }} 
          />
          <div style={{ flex: 1 }}>
            <Typography variant="body2" style={{ color: '#333', fontWeight: 500, marginBottom: 4 }}>
              📷 Question Image
            </Typography>
            <Typography variant="caption" style={{ color: '#666', fontSize: '0.8em' }}>
              This image will be displayed with the question during the exam
            </Typography>
          </div>
          <IconButton
            size="small"
            onClick={handleRemoveImage}
            style={{
              background: '#f44336',
              color: 'white',
              width: 32,
              height: 32
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      )}
      {['mcq-single', 'mcq-multi'].includes(question.type) && (
        <div style={{ marginTop: 16 }}>
          {question.options.map((option, oIndex) => (
            <Grid container spacing={1} key={option.id} alignItems="center">
              <Grid item xs={6}>
                <TextField
                  label={`Option ${oIndex + 1}`}
                  value={option.text}
                  onChange={e => handleOptionChange(oIndex, 'text', e.target.value)}
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={!!validationErrors[`question_${sectionIndex}_${questionIndex}_option_${oIndex}_text`]}
                  helperText={validationErrors[`question_${sectionIndex}_${questionIndex}_option_${oIndex}_text`] ? validationErrors[`question_${sectionIndex}_${questionIndex}_option_${oIndex}_text`] : ''}
                />
              </Grid>
              <Grid item xs={2}>
                {question.type === 'mcq-single' ? (
                  <Radio
                    checked={option.isCorrect || false}
                    onChange={e => handleCorrectAnswerChange(option.id)}
                    color="primary"
                  />
                ) : (
                  <Checkbox
                    checked={option.isCorrect || false}
                    onChange={e => handleOptionChange(oIndex, 'isCorrect', e.target.checked)}
                    color="primary"
                  />
                )}
                <span>Correct</span>
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label="Marks"
                  type="number"
                  value={option.marks}
                  onChange={e => handleOptionChange(oIndex, 'marks', e.target.value)}
                  variant="outlined"
                  size="small"
                  error={!!validationErrors[`question_${sectionIndex}_${questionIndex}_option_${oIndex}_marks`]}
                  helperText={validationErrors[`question_${sectionIndex}_${questionIndex}_option_${oIndex}_marks`] ? validationErrors[`question_${sectionIndex}_${questionIndex}_option_${oIndex}_marks`] : ''}
                />
              </Grid>
              <Grid item xs={2}>
                <IconButton 
                  onClick={() => removeOption(oIndex)}
                  disabled={question.options.length <= 1}
                  title={question.options.length <= 1 ? "Cannot delete - minimum 1 option required" : "Delete option"}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button
            onClick={addOption}
            variant="contained"
            color="secondary"
            style={{ marginTop: 12, fontWeight: 600, borderRadius: 20, minWidth: 140 }}
          >
            Add Option
          </Button>
          {(validationErrors[`question_${sectionIndex}_${questionIndex}_options`] || validationErrors[`question_${sectionIndex}_${questionIndex}_correct`] || validationErrors[`question_${sectionIndex}_${questionIndex}_marks`]) && (
            <Box mt={1} p={1} border={1} borderColor="#f44336" borderRadius={4} bgcolor="#ffebee">
              <Typography variant="body2" style={{ color: '#d32f2f' }}>
                {validationErrors[`question_${sectionIndex}_${questionIndex}_options`] || validationErrors[`question_${sectionIndex}_${questionIndex}_correct`] || validationErrors[`question_${sectionIndex}_${questionIndex}_marks`]}
              </Typography>
            </Box>
          )}
        </div>
      )}
      {question.type === 'text' && (
        <div style={{ marginTop: 16 }}>
          <TextField
            label="Evaluation Points (comma-separated)"
            value={question.evaluationPoints || ''}
            onChange={e => {
              // Store exactly as user types - no restrictions, no conversion
              onQuestionChange(question.id, 'evaluationPoints', e.target.value);
            }}
            variant="outlined"
            fullWidth
            helperText="Enter evaluation criteria for AI evaluation (e.g., clarity, tone, content, structure)"
            placeholder="e.g., clarity, tone, content, structure"
          />
        </div>
      )}
      {question.type === 'scenario-text' && (
        <div style={{ marginTop: 16 }}>
          {/* Debug info */}
          <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
            Debug: Question type = {question.type}, scenarioOptions count = {question.scenarioOptions?.length || 0}
          </Typography>
          <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#1976d2', marginBottom: 8 }}>
            Scenario Options
          </Typography>
          {question.scenarioOptions ? question.scenarioOptions.map((option, oIndex) => (
            <Box key={oIndex} mb={2} p={2} border={1} borderColor="#e0e0e0" borderRadius={6} style={{ background: '#fafbff' }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12}>
                  <TextField
                    label={`Option ${oIndex + 1} Title`}
                    value={option.title || ''}
                    onChange={e => {
                      const updatedOptions = [...question.scenarioOptions];
                      updatedOptions[oIndex] = { ...updatedOptions[oIndex], title: e.target.value };
                      onQuestionChange(question.id, 'scenarioOptions', updatedOptions);
                    }}
                    variant="outlined"
                    fullWidth
                    size="small"
                    style={{ marginBottom: 8 }}
                    error={hasInteracted && !!validationErrors[`question_${sectionIndex}_${questionIndex}_scenario_${oIndex}_title`]}
                    helperText={hasInteracted && validationErrors[`question_${sectionIndex}_${questionIndex}_scenario_${oIndex}_title`] ? validationErrors[`question_${sectionIndex}_${questionIndex}_scenario_${oIndex}_title`] : "Enter the option title (e.g., 'Option 1: Escalation Handling')"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={`Option ${oIndex + 1} Content`}
                    value={option.content || ''}
                    onChange={e => {
                      const updatedOptions = [...question.scenarioOptions];
                      updatedOptions[oIndex] = { ...updatedOptions[oIndex], content: e.target.value };
                      onQuestionChange(question.id, 'scenarioOptions', updatedOptions);
                    }}
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={10}
                    size="small"
                    style={{ marginBottom: 8 }}
                    error={hasInteracted && !!validationErrors[`question_${sectionIndex}_${questionIndex}_scenario_${oIndex}_content`]}
                    helperText={hasInteracted && validationErrors[`question_${sectionIndex}_${questionIndex}_scenario_${oIndex}_content`] ? validationErrors[`question_${sectionIndex}_${questionIndex}_scenario_${oIndex}_content`] : "Enter the scenario description and bullet points"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Evaluation Points (comma-separated)"
                    value={option.evaluationPoints ? option.evaluationPoints.join(', ') : ''}
                    onChange={e => {
                      const points = e.target.value.split(',').map(p => p.trim()).filter(Boolean);
                      const updatedOptions = [...question.scenarioOptions];
                      updatedOptions[oIndex] = { ...updatedOptions[oIndex], evaluationPoints: points };
                      onQuestionChange(question.id, 'scenarioOptions', updatedOptions);
                    }}
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={hasInteracted && !!validationErrors[`question_${sectionIndex}_${questionIndex}_scenario_${oIndex}_evaluationPoints`]}
                    helperText={hasInteracted && validationErrors[`question_${sectionIndex}_${questionIndex}_scenario_${oIndex}_evaluationPoints`] ? validationErrors[`question_${sectionIndex}_${questionIndex}_scenario_${oIndex}_evaluationPoints`] : "Enter evaluation criteria for this scenario (e.g., tone, empathy, next steps)"}
                  />
                </Grid>
                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton 
                    onClick={() => {
                      const updatedOptions = question.scenarioOptions.filter((_, i) => i !== oIndex);
                      onQuestionChange(question.id, 'scenarioOptions', updatedOptions);
                    }}
                    disabled={question.scenarioOptions.length <= 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          )) : (
            <Box mb={2} p={2} border={1} borderColor="#e0e0e0" borderRadius={6} style={{ background: '#fafbff' }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12}>
                  <TextField
                    label="Option 1 Title"
                    value=""
                    onChange={e => {
                      const currentOptions = question.scenarioOptions || [];
                      const updatedOptions = currentOptions.length > 0 ? 
                        currentOptions.map((opt, idx) => idx === 0 ? { ...opt, title: e.target.value } : opt) :
                        [{ title: e.target.value, content: '', evaluationPoints: [] }];
                      onQuestionChange(question.id, 'scenarioOptions', updatedOptions);
                    }}
                    variant="outlined"
                    fullWidth
                    size="small"
                    style={{ marginBottom: 8 }}
                    helperText="Enter the option title (e.g., 'Option 1: Escalation Handling')"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Option 1 Content"
                    value=""
                    onChange={e => {
                      const currentOptions = question.scenarioOptions || [];
                      const updatedOptions = currentOptions.length > 0 ? 
                        currentOptions.map((opt, idx) => idx === 0 ? { ...opt, content: e.target.value } : opt) :
                        [{ title: '', content: e.target.value, evaluationPoints: [] }];
                      onQuestionChange(question.id, 'scenarioOptions', updatedOptions);
                    }}
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={10}
                    size="small"
                    style={{ marginBottom: 8 }}
                    helperText="Enter the scenario description and bullet points"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Evaluation Points (comma-separated)"
                    value=""
                    onChange={e => {
                      const points = e.target.value.split(',').map(p => p.trim()).filter(Boolean);
                      const currentOptions = question.scenarioOptions || [];
                      const updatedOptions = currentOptions.length > 0 ? 
                        currentOptions.map((opt, idx) => idx === 0 ? { ...opt, evaluationPoints: points } : opt) :
                        [{ title: '', evaluationPoints: points }];
                      onQuestionChange(question.id, 'scenarioOptions', updatedOptions);
                    }}
                    variant="outlined"
                    fullWidth
                    size="small"
                    helperText="Enter evaluation criteria for this scenario (e.g., tone, empathy, next steps)"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
          <Button
            onClick={() => {
              const currentOptions = question.scenarioOptions || [];
              onQuestionChange(question.id, 'scenarioOptions', [
                ...currentOptions,
                { title: '', evaluationPoints: [] }
              ]);
            }}
            variant="contained"
            color="secondary"
            style={{ marginTop: 12, fontWeight: 600, borderRadius: 20, minWidth: 140 }}
          >
            Add Scenario Option
          </Button>
          {hasInteracted && validationErrors[`question_${sectionIndex}_${questionIndex}_scenarioOptions`] && (
            <Box mt={1} p={1} border={1} borderColor="#f44336" borderRadius={4} bgcolor="#ffebee">
              <Typography variant="body2" style={{ color: '#d32f2f' }}>
                {validationErrors[`question_${sectionIndex}_${questionIndex}_scenarioOptions`]}
              </Typography>
            </Box>
          )}
        </div>
      )}
      {question.type === 'code' && (
        <div style={{ marginTop: 16 }}>
          <TextField
            label="Problem Description"
            value={question.problemDescription || ''}
            onChange={e => onQuestionChange(question.id, 'problemDescription', e.target.value)}
            variant="outlined"
            fullWidth
            multiline
            minRows={4}
            style={{ marginBottom: 16 }}
            error={hasInteracted && !!validationErrors[`question_${sectionIndex}_${questionIndex}_problemDescription`]}
            helperText={hasInteracted && validationErrors[`question_${sectionIndex}_${questionIndex}_problemDescription`] ? validationErrors[`question_${sectionIndex}_${questionIndex}_problemDescription`] : ''}
          />
          <TextField
            label="Instructions (optional)"
            value={question.instructions || ''}
            onChange={e => onQuestionChange(question.id, 'instructions', e.target.value)}
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            style={{ marginBottom: 16 }}
          />
        </div>
      )}

      {question.type === 'video' && (
        <div style={{ marginTop: 16 }}>
          {/* Instructions Field */}
          <TextField
            label="Instructions for Candidate"
            value={question.instructions || ''}
            onChange={e => onQuestionChange(question.id, 'instructions', e.target.value)}
            variant="outlined"
            fullWidth
            multiline
            minRows={4}
            style={{ marginBottom: 16 }}
            helperText="Provide step-by-step instructions for the video recording"
          />

          {/* Evaluation Points Section (same as text questions) */}
                  <TextField
            label="Evaluation Points (comma-separated)"
            value={question.evaluationPoints || ''}
                    onChange={e => {
              // Store exactly as user types - no restrictions, no conversion
              onQuestionChange(question.id, 'evaluationPoints', e.target.value);
                    }}
                    variant="outlined"
                    fullWidth
            style={{ marginBottom: 16 }}
            helperText="Enter evaluation criteria for AI evaluation (e.g., clarity, tone, content, structure)"
            placeholder="e.g., clarity, tone, content, structure"
          />
        </div>
      )}
      {question.type === 'psyco' && (
        <div style={{ marginTop: 16 }}>
          {/* Debug info */}
          <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
            Debug: Question type = {question.type}, Text length = {question.text?.length || 0}
          </Typography>
          <TextField
            label="Description (Include all options A, B, C, D and scenarios)"
            value={question.problemDescription || ''}
            onChange={e => onQuestionChange(question.id, 'problemDescription', e.target.value)}
            variant="outlined"
            fullWidth
            multiline
            minRows={4}
            style={{ marginBottom: 12 }}
            helperText="Enter all the options and scenarios for this psyco question. Include options A, B, C, D with their descriptions."
          />
          <TextField
            label="Correct Pattern (e.g. ABCD, BADC)"
            value={question.psycoAnswer || ''}
            onChange={e => {
              const val = e.target.value.toUpperCase();
              if (/^[ABCD]{0,4}$/.test(val)) {
                onQuestionChange(question.id, 'psycoAnswer', val);
              }
            }}
            error={hasInteracted && (!!validationErrors[`question_${sectionIndex}_${questionIndex}_psycoAnswer`] || (!!question.psycoAnswer && (question.psycoAnswer.length !== 4 || new Set(question.psycoAnswer).size !== 4)))}
            helperText={hasInteracted && (validationErrors[`question_${sectionIndex}_${questionIndex}_psycoAnswer`] || (!!question.psycoAnswer && (question.psycoAnswer.length !== 4 || new Set(question.psycoAnswer).size !== 4) ? 'Must be 4 unique letters (A, B, C, D)' : ''))}
            variant="outlined"
            fullWidth
            style={{ marginBottom: 12 }}
            inputProps={{ maxLength: 4, style: { letterSpacing: 4, textTransform: 'uppercase' } }}
          />
        </div>
      )}
      {/* AI Evaluation Checkbox */}
      {['text', 'scenario-text', 'code', 'video'].includes(question.type) && (
        <FormControlLabel
          control={
            <Checkbox
              checked={question.enableAIEvaluation || false}
              onChange={(e) => onQuestionChange(question.id, 'enableAIEvaluation', e.target.checked)}
              name="enableAIEvaluation"
              color="primary"
            />
          }
          label="Enable AI-Powered Evaluation for this question"
        />
      )}
    </Box>
  );
};

export default QuestionEditor; 